import type { Me } from '../api/auth';
import { ca } from '../i18n/ca';

/** A Bootstrap Icons name without the `bi-` prefix (https://icons.getbootstrap.com). */
export type IconName = string;

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** Present in the bottom bar on phones. */
  bar?: boolean;
  /** An absolute address outside the app (the panel); the shell opens it in another tab. */
  external?: boolean;
}

const has = (me: Me, ...roles: Me['roles']) => roles.some((r) => me.roles.includes(r));

/**
 * The rail's items for this account: the information architecture of UI doc section 4, gated by
 * role. `panelHost` comes from `/api/meta`; given it, an account with `PANEL_ACCESS` gets a way
 * out to the panel right after the servers. The function stays pure: everything it needs is an
 * argument.
 */
export function navItems(me: Me, panelHost?: string): NavItem[] {
  const items: NavItem[] = [
    { to: '/', label: ca.nav.home, icon: 'house-door', end: true, bar: true },
  ];
  if (me.authenticated) {
    items.push({ to: '/sessions', label: ca.nav.sessions, icon: 'play-circle', bar: true });
    items.push({ to: '/library', label: ca.nav.library, icon: 'collection', bar: true });
  }
  if (has(me, 'EDITOR', 'ADMIN')) {
    items.push({ to: '/new', label: ca.nav.newMission, icon: 'plus-lg' });
    items.push({ to: '/catalog', label: ca.nav.catalog, icon: 'grid' });
  }
  if (has(me, 'SERVER_MANAGER', 'ADMIN')) {
    items.push({ to: '/servers', label: ca.nav.servers, icon: 'hdd-stack' });
  }
  if (panelHost && has(me, 'PANEL_ACCESS', 'ADMIN')) {
    items.push({
      to: `https://${panelHost}/`,
      label: ca.nav.panel,
      icon: 'box-arrow-up-right',
      external: true,
    });
  }
  // The tests: everything for an admin; for anybody else the cards they took,
  // and nothing in the rail until they have taken one (the owner, 2026-09-29).
  // A tester reads the finished work there, cards of their own or none.
  if (me.authenticated && (has(me, 'ADMIN', 'TESTER') || me.tests === true)) {
    items.push({ to: '/proves', label: ca.nav.tests, icon: 'controller' });
  }
  if (has(me, 'ADMIN')) items.push({ to: '/admin', label: ca.nav.admin, icon: 'shield-check' });
  return items;
}

/**
 * The footer's links: what is worth finding and never worth a place in the rail
 * (the owner, 2026-09-25). The changelog for anybody; the public archive of
 * reports for a member, whose rail lost it on 2026-09-24 (a visitor has none: the archive and
 * its API need an account); the status page for an admin, whose rail
 * lost it the same day. The footer exists on desktop only, so nothing here is a
 * page a phone needs: the changelog reaches a phone through Discord's own link.
 */
export function footerItems(me: Me): NavItem[] {
  // Nothing of the platform is open to a visitor (the owner, 2026-10-12): the footer is a member's.
  const items: NavItem[] = [];
  if (me.authenticated) {
    items.push({ to: '/novetats', label: ca.nav.changelog, icon: 'megaphone' });
    items.push({ to: '/full-de-ruta', label: ca.nav.roadmap, icon: 'signpost-split' });
    items.push({ to: '/reports', label: ca.nav.reports, icon: 'file-earmark-text' });
  }
  if (has(me, 'ADMIN')) {
    items.push({ to: '/estat', label: ca.nav.status, icon: 'clipboard-check' });
  }
  return items;
}

/** Profile and settings for a member; the way in for a visitor. */
export function accountItems(me: Me): NavItem[] {
  if (!me.authenticated)
    return [{ to: '/signin', label: ca.nav.signIn, icon: 'box-arrow-in-right', bar: true }];
  return [
    { to: `/u/${me.handle ?? ''}`, label: ca.nav.profile, icon: 'person', bar: true },
    { to: '/settings', label: ca.nav.settings, icon: 'gear' },
  ];
}
